import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { INTEGRATION_ACTIONS } from '@app/components/integration-card/integration-card.component';
import { IntegrationType } from '@app/models/common/integration-type.model';
import { SettingsIntegrationBase } from '@app/models/company/settings/integrations/integration-base.model';
import { SettingsIntegration } from '@app/models/company/settings/settings-integration.model';
import { BaseForm } from '@app/modules/forms/base.form';
import { GreenhouseForm, IndeedForm, LinkedinForm, WorkableForm } from '@app/modules/forms/settings/components';
import { ActiveIntegrations } from '@app/modules/settings/enums/integration.enum';
import { XeroConnectionStatus } from '@app/modules/settings/enums/xero-connection-status.enum';
import { ResthookAPIService, SettingsIntegrationService, XeroIntegrationService } from '@app/modules/settings/services';
import { SettingsMenuService } from '@app/modules/settings/services/settings-menu.service';
import { AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import ConnectionStatus from '@app/types/connection-status.type';
import { forOwn, keyBy, pick } from 'lodash-es';

const SETTINGS_INTEGRATION_ACTIONS = {
    ...INTEGRATION_ACTIONS,
    MANAGE: 'settings.integration-card.manageButton',
    MANAGE_XERO: 'settings.integration-card.xero.accountMappingButton',
    RECONNECT: 'settings.integration-card.reconnectButton',
    CONTINUE: 'settings.integration-card.continueButton',
    DISCONNECT: 'settings.integration-card.disconnectButton',
    XERO_ERRORS: 'settings.integration-card.xero.errorsAndEventsLogButton',
};
type IntegrationAction = keyof typeof SETTINGS_INTEGRATION_ACTIONS;

@Component({
    templateUrl: './integration-v2-index.template.html',
    styleUrls: ['./integration-v2-index.style.scss'],
})
export class IntegrationIndexViewV2 implements OnInit {
    @ViewChild('workableForm') workableFormModal: WorkableForm;
    @ViewChild('greenhouseForm') greenhouseFormModal: GreenhouseForm;
    @ViewChild('linkedinForm') linkedinFormModal: LinkedinForm;
    @ViewChild('indeedForm') indeedFormModal: IndeedForm;

    isLoading = false;
    integrations: { [integrationName: string]: SettingsIntegration } = {};
    integrationTypes: { [integrationName: string]: IntegrationType } = {};
    isIndeedIntegrationModalOpen = false;
    isWorkableModalOpen = false;
    isJazzHRModalOpen = false;
    isGreenhouseModalOpen = false;
    isIndeedDisconnectModalOpen = false;
    isXeroModalOpen = false;
    isXeroDisconnectModalOpen = false;
    isZapierModalOpen = false;
    isLinkedinModalOpen = false;
    canSubmitLinkedin = false;
    zapierApiKey = '<api Key>';
    activeIntegrations = ActiveIntegrations;
    xeroOAuthUrl = '';
    xeroCompanyName = '';
    xeroConnectionStatus?: XeroConnectionStatus;

    constructor(
        public auth: AuthService,
        private notify: NotifyService,
        private integrationService: SettingsIntegrationService,
        private xeroIntegrationService: XeroIntegrationService,
        private route: ActivatedRoute,
        private router: Router,
        private resthooksApiService: ResthookAPIService,
        public settingsMenuService: SettingsMenuService,
        private featureService: FeatureService
    ) {}

    async ngOnInit(): Promise<void> {
        // Integration types and integrations can be retrieved at the same time
        const [isLinkedInDisabled, isIndeedDisabled] = await Promise.all([
            this.featureService.has('disable-new-linked-in-connections'),
            this.featureService.has('disable-indeed-integration'),
            this.loadIntegrations(),
            this.loadIntegrationTypes(),
        ]);

        forOwn(this.integrationTypes, (integrationType, integrationName) => {
            if (integrationName === ActiveIntegrations.linkedin && isLinkedInDisabled) {
                // Only disable new connection
                integrationType.disabled = this.integrationTypeInUse(integrationType) !== 'connected';
            }
            if (integrationName === ActiveIntegrations.indeed && isIndeedDisabled) {
                // Disable all connections
                integrationType.disabled = true;
            }

            // All integrations are loaded at this point except Xero which needs an additional API call to get the company name
            if (integrationName !== ActiveIntegrations.xero) {
                // isLoading sets spinner at individual integration card level
                integrationType.isLoading = false;
            }
        });

        this.getXeroCompanyName();

        const indeedIntegration = this.integrations[ActiveIntegrations.indeed];

        if (!indeedIntegration) {
            return;
        }

        if (!this.integrationTypeInUse(indeedIntegration.integrationType)) {
            return;
        }
    }

    async loadIntegrations(): Promise<void> {
        this.integrations = keyBy(
            await this.integrationService.getIntegrations(),
            (integration) => integration.integrationType.name
        );
    }

    loadIntegrationTypes(): Promise<void> {
        this.isLoading = true;
        return this.integrationService.getIntegrationTypes().then((integrationTypes) => {
            // index integrationTypes by name for faster retrieval
            this.integrationTypes = keyBy(integrationTypes, 'name');
            // isLoading sets spinner at page level
            this.isLoading = false;
        });
    }

    getAvailableActions(integrationType: IntegrationType): Partial<typeof SETTINGS_INTEGRATION_ACTIONS> {
        const actions: IntegrationAction[] = [];
        const connectionState = this.integrationTypeInUse(integrationType);

        if (connectionState === 'disconnected') {
            return pick(SETTINGS_INTEGRATION_ACTIONS, 'CONNECT');
        }

        if (connectionState !== 'errored') {
            actions.push('DISCONNECT');
        } else {
            actions.push('RECONNECT');
        }
        if (this.isManageable(integrationType)) {
            actions.push(integrationType.name === 'xero' ? 'MANAGE_XERO' : 'MANAGE');
        }
        if (integrationType.name === 'xero') {
            actions.push('XERO_ERRORS');
        }
        if (connectionState === 'incomplete') {
            actions.push('CONTINUE');
        }

        return pick(SETTINGS_INTEGRATION_ACTIONS, actions);
    }

    onIntegrationEvent(connectionEvent: IntegrationAction, integrationType: IntegrationType): void {
        switch (connectionEvent) {
            case 'CONNECT':
            case 'RECONNECT':
                this.onConnect(integrationType);
                break;
            case 'DISCONNECT':
                this.onDisconnect(integrationType);
                break;
            case 'CONTINUE':
                this.onContinue();
                break;
            case 'MANAGE':
            case 'MANAGE_XERO':
                this.onManage(integrationType);
                break;
            case 'XERO_ERRORS':
                this.router.navigate(['/settings/integrations-v2/xero/errors-and-events-log']);
                break;
        }
    }

    async onConnect(integrationType: IntegrationType): Promise<void> {
        switch (integrationType.name) {
            case this.activeIntegrations.indeed:
                this.openIndeedManageModel();
                this.indeedFormModal.reset();
                break;

            case this.activeIntegrations.greenhouse:
                this.openGreenhouseManageModal();
                this.greenhouseFormModal.reset();
                break;

            case this.activeIntegrations.workable:
                this.openWorkableManageModal();
                this.workableFormModal.reset();
                break;

            case this.activeIntegrations.xero:
                this.openXeroModal();
                break;

            case this.activeIntegrations.zapier:
                this.openZapierModal();
                this.zapierApiKey = await this.generateResthooksApiKey(1);
                break;
            case this.activeIntegrations.linkedin:
                this.isLinkedinModalOpen = true;
                this.linkedinFormModal.reset();
                break;
        }
    }

    async onManage(integrationType: IntegrationType): Promise<void> {
        const integration = this.getIntegration(integrationType);
        if (!integration) {
            return;
        }
        switch (integrationType.name) {
            case this.activeIntegrations.indeed:
                this.indeedFormModal.reset();
                this.indeedFormModal.setId(integration.integrationDetailId);
                this.openIndeedManageModel();
                break;

            case this.activeIntegrations.greenhouse:
                this.greenhouseFormModal.reset();
                this.greenhouseFormModal.setId(integration.integrationDetailId);
                this.openGreenhouseManageModal();
                break;

            case this.activeIntegrations.workable:
                this.workableFormModal.reset();
                this.workableFormModal.setId(integration.integrationDetailId);
                this.openWorkableManageModal();
                break;

            case this.activeIntegrations.xero:
                await this.router.navigate(['/settings/integrations-v2/xero/manage']);
                break;
            case this.activeIntegrations.linkedin:
                this.linkedinFormModal.reset();
                this.linkedinFormModal.findIntegrationById(integration.integrationDetailId);
                this.isLinkedinModalOpen = true;
                break;
        }
    }

    async onDisconnect(integrationType: IntegrationType): Promise<void> {
        const integration = this.getIntegration(integrationType);
        if (!integration) {
            return;
        }

        if (integration.integrationType.name === 'indeed') {
            this.isIndeedDisconnectModalOpen = true;
            return;
        }

        if (integrationType.name === 'xero') {
            this.isXeroDisconnectModalOpen = true;
            return;
        }

        this.integrationTypes[integrationType.name].isLoading = true;
        try {
            await this.integrationService.onDeleteIntegration(integration);
            await this.loadIntegrations();
        } catch (err) {
            this.notify.error(err.message || 'settings.integration.integrationNotUpdated');
        } finally {
            this.integrationTypes[integrationType.name].isLoading = false;
        }
    }

    async onCreate(
        integrationForm: BaseForm,
        integrationDetail: SettingsIntegrationBase,
        integrationTypeName: string
    ): Promise<void> {
        if (typeof integrationForm.form !== 'undefined') {
            integrationForm.submit();

            if (!integrationForm.form?.valid) {
                return;
            }
        }

        const integrationType = this.integrationTypes[integrationTypeName];
        integrationType.isLoading = true;

        try {
            await this.integrationService.onManage(integrationDetail);
            this.notify.success('settings.integration.integrationUpdated');
            await this.loadIntegrations();
            this.closeModal(integrationType.name);
        } catch (err) {
            this.notify.error(err.message || 'settings.integration.couldNotSave');
        } finally {
            integrationType.isLoading = false;
        }
    }

    getIntegration(integrationType: IntegrationType): SettingsIntegration | undefined {
        return this.integrations[integrationType.name];
    }

    openIndeedManageModel(): void {
        this.isIndeedIntegrationModalOpen = true;
    }

    openWorkableManageModal(): void {
        this.isWorkableModalOpen = true;
    }

    openGreenhouseManageModal(): void {
        this.isGreenhouseModalOpen = true;
    }

    openJazzHRManageModal(): void {
        this.isJazzHRModalOpen = true;
    }

    openXeroModal(): void {
        this.xeroIntegrationService.getOAuthUrl().then((url) => {
            this.xeroOAuthUrl = url;
            this.isXeroModalOpen = true;
        });
    }

    openZapierModal(): void {
        this.isZapierModalOpen = true;
    }

    closeModal(name: string): void {
        switch (name) {
            case this.activeIntegrations.indeed:
                this.isIndeedIntegrationModalOpen = false;
                break;

            case this.activeIntegrations.greenhouse:
                this.isGreenhouseModalOpen = false;
                break;

            case this.activeIntegrations.workable:
                this.isWorkableModalOpen = false;
                break;

            case this.activeIntegrations.xero:
                this.isXeroModalOpen = false;
                break;

            case this.activeIntegrations.zapier:
                this.isZapierModalOpen = false;
                break;
            case this.activeIntegrations.linkedin:
                this.isLinkedinModalOpen = false;
                break;
        }
    }

    integrationTypeInUse(integrationType: IntegrationType): ConnectionStatus | undefined {
        if (integrationType.isLoading) {
            return undefined;
        }

        const integration = this.getIntegration(integrationType);

        if (integration && !integration.isEnabled) {
            switch (integrationType.name) {
                case ActiveIntegrations.indeed:
                    return 'incomplete';
                case ActiveIntegrations.linkedin:
                    return 'pendingApproval';
            }
        }

        if (integration && integrationType.name === ActiveIntegrations.xero) {
            switch (this.xeroConnectionStatus) {
                case 'connected':
                    return 'connected';
                case 'disconnected':
                    return 'errored';
                default:
                    return 'disconnected';
            }
        }

        return integration ? 'connected' : 'disconnected';
    }

    isManageable(integrationType: IntegrationType): boolean {
        return !this.integrationTypeInUse(integrationType) ? false : true;
    }

    async onDisconnectIndeed(): Promise<void> {
        const integrationType = this.integrationTypes[ActiveIntegrations.indeed];
        integrationType.isLoading = true;

        try {
            this.isIndeedDisconnectModalOpen = false;
            const integration = this.integrations[integrationType.name];
            await this.integrationService.onDeleteIntegration(integration);
            await this.loadIntegrations();
        } catch (err) {
            this.notify.error(err.message || 'settings.integration.integrationNotUpdated');
        } finally {
            integrationType.isLoading = false;
        }
    }

    async onDisconnectXero(): Promise<void> {
        this.integrationTypes[ActiveIntegrations.xero].isLoading = true;

        try {
            this.isXeroDisconnectModalOpen = false;
            await this.xeroIntegrationService.revokeAccess();
            await this.loadIntegrations();
            this.xeroCompanyName = '';
            delete this.xeroConnectionStatus;
        } catch (err) {
            this.notify.error(err.message || 'settings.integration.couldNotDisconnectXero');
        } finally {
            this.integrationTypes[ActiveIntegrations.xero].isLoading = false;
        }
    }

    /** @deprecated */
    onContinue(): void {
        // used to redirect to the Indeed manage page, now it does nothing
    }

    private async generateResthooksApiKey(integrationId: number): Promise<string> {
        const resthookApiResponse = await this.resthooksApiService.generateApiKey(integrationId);

        return resthookApiResponse.value;
    }

    private async getXeroCompanyName(): Promise<void> {
        try {
            if (!this.integrationTypes.xero) {
                return;
            }

            this.integrationTypes[ActiveIntegrations.xero].isLoading = true;
            const integrationDetails = await this.xeroIntegrationService.getXeroIntegrationDetails(
                Object.values(this.integrationTypes),
                Object.values(this.integrations)
            );

            if (integrationDetails) {
                this.xeroCompanyName = integrationDetails.xeroCompanyName;
                this.xeroConnectionStatus = integrationDetails.xeroConnectionStatus;
            }
        } catch {
            this.xeroConnectionStatus = 'disconnected';
        }
        this.integrationTypes[ActiveIntegrations.xero].isLoading = false;
    }
}
